import { FC, createContext, useContext } from 'react';

import { useLocalStorage } from 'support/react/hooks';

interface Rating {
  sweetId: string;
  author: string;
  rating: number;
  text: string;
}

interface RatingsContextType {
  reviews: { [T: string]: Rating[] };
  addReview(rating: Rating): void;
  getReviews(sweetId: string): Rating[];
  getAverageRating(sweetId: string): { average: number | null; reviews: number | null };
}

export const RatingsContext = createContext<RatingsContextType>({
  reviews: {},
  addReview: () => {},
  getReviews: () => [],
  getAverageRating: () => ({ average: null, reviews: null }),
});

export const RatingsContextProvider: FC = ({ children }) => {
  const [reviews, setItems] = useLocalStorage<RatingsContextType['reviews']>('toy_reviews', { initialValue: {} });

  const addReview: RatingsContextType['addReview'] = newItem => {
    const nextItems = { ...reviews };

    if (!nextItems[newItem.sweetId]) {
      nextItems[newItem.sweetId] = [];
    }

    nextItems[newItem.sweetId].push(newItem);

    setItems(nextItems);
  };

  const getReviews: RatingsContextType['getReviews'] = (sweetId: string) => reviews[sweetId] || [];

  const getAverageRating: RatingsContextType['getAverageRating'] = (sweetId: string) => {
    const sweetReviews = getReviews(sweetId);

    const sweetStats = sweetReviews.reduce<{ reviews: number; total: number }>(
      (prev, curr) => {
        return {
          reviews: prev.reviews + 1,
          total: prev.total + curr.rating,
        };
      },
      { reviews: 0, total: 0 },
    );

    return {
      reviews: sweetStats.reviews,
      average: sweetStats.total / sweetStats.reviews,
    };
  };

  return (
    <RatingsContext.Provider value={{ reviews, addReview, getReviews, getAverageRating }}>
      {children}
    </RatingsContext.Provider>
  );
};

export const useRatingsContext = () => {
  return useContext(RatingsContext);
};
